import { useMemo } from "react";

import { IQuotation } from "pages/shipper/ShipmentQuotes/components/quotes/QuotesType";
import { replaceLinebreaks } from "helpers/commonHelpers";
import { useGetCostDetails } from "api/queries/quotation/useGetCostDetails";
import usePermissions from "hooks/usePermissions";
import ExpandableText from "components/atoms/ExpandableText/ExpandableText";
import CostConversionRates from "pages/shipper/ShipmentQuotes/components/CostDetailDialog/components/CostConversionRates";
import QuotationDetailChargesTable from "./QuotationDetailChargesTable";

type QuotationDetailChargesProps = {
  currentQuotation: IQuotation;
};

function QuotationDetailCharges(props: QuotationDetailChargesProps) {
  const { currentQuotation } = props;

  const { permissions } = usePermissions();
  const { user_type, permissions: permissionData } = JSON.parse(permissions || "{}");
  const canViewQuotePricing = (permissionData && permissionData?.booking?.view_quote_pricing) || user_type === "provider";

  const { data: costDetailsData, isLoading: costDetailsLoading } = useGetCostDetails(currentQuotation.reference, {
    enabled: !!currentQuotation.reference,
  });

  const hasPostBookingCharges = useMemo(
    () =>
      Object.keys(costDetailsData?.costs?.post_booking_in_transit_costs_data ?? {}).some(
        (key: any) => costDetailsData?.costs?.post_booking_in_transit_costs_data?.[key]?.length > 0,
      ),
    [costDetailsData],
  );

  return (
    <div className="px-3xl">
      <div className="my-xl text-lg font-semibold text-primary">Charges and costs</div>
      {currentQuotation?.other?.attachments?.length > 0 && (
        <>
          <div className="text-xs font-medium text-secondary-v2">Attachments</div>
          {currentQuotation.other.attachments.map((attachment) => (
            <div className="text-sm text-brand-secondary">
              <a
                href={attachment.file_url}
                key={attachment.file_url}
                target="_blank"
                style={{ fontSize: "11px", width: "170px", whiteSpace: "initial" }}
                rel="noreferrer"
              >
                {attachment.filename}
              </a>
            </div>
          ))}
        </>
      )}
      {costDetailsData?.not_included_in_price && canViewQuotePricing && (
        <div className="my-3xl">
          <div className="text-xs font-medium text-secondary-v2">Not included</div>
          <ExpandableText
            content={replaceLinebreaks(costDetailsData.not_included_in_price || "-")}
            maxLines={10}
            showMoreText="Show all"
            showLessText="Show less"
          />
        </div>
      )}
      {costDetailsData?.additional_information && (
        <div>
          <div className="text-xs font-medium text-secondary-v2">Additional information</div>
          <ExpandableText
            content={replaceLinebreaks(costDetailsData.additional_information || "-")}
            maxLines={10}
            showMoreText="Show all"
            showLessText="Show less"
          />
        </div>
      )}
      {canViewQuotePricing && (
        <div className="my-3xl">
          <QuotationDetailChargesTable
            costDetailsData={costDetailsData}
            costDetailsLoading={costDetailsLoading}
            costDataKey="quotation_cost_items"
          />
          {hasPostBookingCharges && (
            <div className="mt-xl">
              <div className="text-sm font-medium text-primary mb-sm">Post booking charges</div>
              <QuotationDetailChargesTable
                costDetailsData={costDetailsData}
                costDetailsLoading={costDetailsLoading}
                costDataKey="post_booking_in_transit_costs_data"
              />
            </div>
          )}
          {costDetailsData && (
            <div className="mt-md">
              <CostConversionRates costDetailsData={costDetailsData} v2Format />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default QuotationDetailCharges;
